/**
 * D08 — Donor Information.
 *
 * Adds the anonymous-donation checkbox the phone artwork omitted (gap G-02) —
 * it appeared only on the desktop checkout, though the master prompt requires
 * it everywhere — and the per-donation photo consent that the proof screen
 * later gates on (gap G-18).
 *
 * Validation comes from `@mfa/domain`, the same module the server contract is
 * written against, so the client and server agree on what a valid donor is.
 */

import { useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { isValid, updatesReachable, validateDonor, type DonorDetails } from '@mfa/domain';
import { space } from '../../theme';
import { Button } from '../../components/common/Button';
import { Card } from '../../components/common/Card';
import { Checkbox } from '../../components/forms/Choice';
import { CheckoutHeader } from '../../components/donation/CheckoutHeader';
import { Field } from '../../components/forms/Field';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { useAppConfig } from '../../core/api/hooks';
import { useDonationStore } from '../donation/donationStore';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';
import { SelectField } from '../../components/forms/SelectField';
import { useLocalized } from '../../core/i18n/content';

const EMPTY_DONOR: DonorDetails = {
  fullName: '',
  mobileNumber: '',
  email: '',
  cityId: null,
  cityName: null,
  donorCity: null,
  anonymous: false,
  // Off by default. Every donor field is optional, so a blank form has to be
  // submittable — and updates on by default made "no email" an error, which
  // is precisely the requirement it would have broken. Ticking it is what
  // asks for an email, and that is a rule a donor can see the reason for.
  receiveUpdates: false,
  shareDistributionPhotos: true,
};

/** Sentinel for the select; never sent to the server, which takes a name. */
const OTHER_CITY = '__other__';

export function DonorInformationScreen({ navigation }: DonorScreenProps<'DonorInformation'>) {
  const t = useT();
  const pick = useLocalized();
  const { data: config } = useAppConfig();


  const storedDonor = useDonationStore((s) => s.donor);
  const setDonor = useDonationStore((s) => s.setDonor);

  const [form, setForm] = useState<DonorDetails>(storedDonor ?? EMPTY_DONOR);
  const [touched, setTouched] = useState(false);
  // "Other" is not a city: it is the donor saying the list does not have
  // theirs, and what they then type is the answer.
  const [typingCity, setTypingCity] = useState(false);
  const knownCity = (config?.donorCities ?? []).some((city) => city.name === form.donorCity);
  const donorCityValue = typingCity ? OTHER_CITY : knownCity ? form.donorCity : null;

  const errors = useMemo(() => {
    if (!config) return {};
    // The city moved to the next step, so it cannot gate this one. Validating
    // it here left Continue silently refusing on a field that is not on the
    // screen — the worst kind of dead button. The recipient step checks it,
    // and the server checks it again on submission.
    const { cityId: _city, ...rest } = validateDonor(form, config);
    return rest;
  }, [form, config]);

  const update = <K extends keyof DonorDetails>(key: K, value: DonorDetails[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const onContinue = () => {
    setTouched(true);
    // The same errors the screen shows, which excludes the city: gating on a
    // field that is not on this screen makes Continue refuse with nothing to
    // correct.
    if (!config || !isValid(errors)) return;
    // Nowhere to send an update without an address, so the preference is not
    // carried forward as one the charity has to honour.
    setDonor({ ...form, receiveUpdates: updatesReachable(form) });
    navigation.navigate('RecipientInformation');
  };

  const show = (field: keyof DonorDetails) => (touched ? errors[field] : undefined);

  return (
    <ScreenShell
      leading="back"
      onBackPress={navigation.goBack}
      testID="screen-donor-information"
      subHeader={<CheckoutHeader current={1} title={t('Donor Information')} subtitle={t('All optional — donate anonymously if you prefer')} />}
      pinnedFooter={
        <Button
          label={t('Continue to Recipient Info')}
          onPress={onContinue}
          disabled={touched && !isValid(errors)}
          testID="cta-donor-continue"
        />
      }
    >


      <View style={styles.fields}>
        <Field
          label={t('Full Name')}
          icon="person"
          value={form.fullName ?? ''}
          onChangeText={(value) => update('fullName', value)}
          placeholder={t('Optional')}
          autoComplete="name"
          error={show('fullName')}
        />

        <Field
          label={t('Mobile Number')}
          icon="phone"
          value={form.mobileNumber ?? ''}
          onChangeText={(value) => update('mobileNumber', value)}
          placeholder={t('Optional')}
          keyboardType="phone-pad"
          autoComplete="tel"
          error={show('mobileNumber')}
        />

        <Field
          label={t('Email Address')}
          icon="email"
          value={form.email ?? ''}
          onChangeText={(value) => update('email', value)}
          placeholder={t('Optional')}
          keyboardType="email-address"
          autoCapitalize="none"
          autoComplete="email"
          error={show('email')}
        />

        {/* Where the donor is from — not where the meals go. Optional, and
            Other sits at the top because the list cannot name everywhere. */}
        <SelectField
          label={t('Your City')}
          placeholder={t('Optional')}
          icon="location"
          testID="select-donor-city"
          value={donorCityValue}
          options={[
            { value: OTHER_CITY, label: t('Other (type your city)') },
            ...(config?.donorCities ?? []).map((city) => ({
              value: city.name,
              label: pick(city.name, city.nameUr),
            })),
          ]}
          onChange={(value) => {
            setTypingCity(value === OTHER_CITY);
            update('donorCity', value === OTHER_CITY ? '' : value);
          }}
        />

        {typingCity ? (
          <Field
            label={t('Your City')}
            icon="location"
            value={form.donorCity ?? ''}
            onChangeText={(value) => update('donorCity', value)}
            placeholder={t('Type your city')}
            testID="field-donor-city"
          />
        ) : null}
      </View>

      <Card title={t('Privacy & Updates')} style={styles.card}>
        <Checkbox
          testID="checkbox-updates"
          checked={form.receiveUpdates}
          onChange={(checked) => update('receiveUpdates', checked)}
          label={t('I would like to receive updates about my donation and impact.')}
          // A note, not an error: the donation goes ahead either way, and
          // the wish is dropped at checkout rather than recorded unsendable.
          description={
            form.receiveUpdates && !form.email?.trim()
              ? t('Add an email address if you would like to receive these.')
              : undefined
          }
        />

        <Checkbox
          testID="checkbox-anonymous"
          checked={form.anonymous}
          onChange={(checked) => update('anonymous', checked)}
          label={t('Please keep me anonymous')}
          description={t('Your name will not be shown anywhere.')}
        />

        {config?.proofPhotoConsentEnabled ? (
          <Checkbox
            testID="checkbox-photos"
            checked={form.shareDistributionPhotos}
            onChange={(checked) => update('shareDistributionPhotos', checked)}
            label={t('Send me photos from the distribution')}
            description={t('You can change this at any time in Settings.')}
          />
        ) : null}
      </Card>

      {/* No order summary here. The stepper says which step this is, Order
          Details carries the meal and the total, Review shows the whole thing
          again before anything is charged, and the pinned bar has the total
          throughout — a fourth copy at the foot of a form only lengthens the
          form. */}
    </ScreenShell>
  );
}

const styles = StyleSheet.create({
  subtitle: {
    marginTop: space.xs,
    marginBottom: space.xl,
  },
  fields: {
    gap: space.lg,
  },
  card: {
    marginTop: space.xl,
  },
});
